import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as api from '../lib/api';
import type { Adventure, Entry, UserIdentity } from '../types';

interface Props {
  adventure: Adventure;
  user: UserIdentity;
  onUpdate: () => void;
  onDelete: () => void;
}

// ── Leaflet map component (CDN-loaded, no npm dep) ──

function AdventureMap({ entries }: { entries: Entry[] }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  const located = entries.filter(e => e.lat != null && e.lng != null);

  const initMap = useCallback(() => {
    if (!mapRef.current || located.length === 0) return;
    const L = (window as any).L;
    if (!L) return;

    // Destroy previous instance
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    // Color gradient from green → yellow → orange → red
    const gradientColors = (t: number): string => {
      // t goes from 0 (start/green) to 1 (end/red)
      if (t < 0.33) {
        // green → yellow
        const p = t / 0.33;
        const r = Math.round(34 + p * (234 - 34));
        const g = Math.round(197 + p * (179 - 197));
        const b = Math.round(94 + p * (8 - 94));
        return `rgb(${r},${g},${b})`;
      } else if (t < 0.66) {
        // yellow → orange
        const p = (t - 0.33) / 0.33;
        const r = Math.round(234 + p * (234 - 234));
        const g = Math.round(179 + p * (88 - 179));
        const b = Math.round(8 + p * (12 - 8));
        return `rgb(${r},${g},${b})`;
      } else {
        // orange → red
        const p = (t - 0.66) / 0.34;
        const r = Math.round(234 + p * (220 - 234));
        const g = Math.round(88 + p * (38 - 88));
        const b = Math.round(12 + p * (38 - 12));
        return `rgb(${r},${g},${b})`;
      }
    };

    const coords: [number, number][] = [];

    located.forEach((entry, i) => {
      const lat = entry.lat!;
      const lng = entry.lng!;
      coords.push([lat, lng]);

      const t = located.length > 1 ? i / (located.length - 1) : 0;
      const markerColor = gradientColors(t);

      const label = entry.city || entry.header;
      const marker = L.circleMarker([lat, lng], {
        radius: 6,
        fillColor: markerColor,
        color: '#fff',
        weight: 2,
        fillOpacity: 0.9,
      }).addTo(map);

      marker.bindPopup(
        `<div style="font-family:sans-serif;font-size:13px;">
          <strong>${i + 1}. ${entry.header}</strong>
          ${label !== entry.header ? `<br/><span style="color:#666">${label}</span>` : ''}
          <br/><span style="color:#999;font-size:11px">${lat.toFixed(4)}, ${lng.toFixed(4)}</span>
        </div>`,
        { maxWidth: 220 }
      );
    });

    // Draw gradient route line — individual segments
    if (coords.length > 1) {
      for (let i = 0; i < coords.length - 1; i++) {
        const t = i / (coords.length - 1);
        L.polyline([coords[i], coords[i + 1]], {
          color: gradientColors(t),
          weight: 3,
          opacity: 0.7,
        }).addTo(map);
      }
    }

    // Fit bounds
    if (coords.length === 1) {
      map.setView(coords[0], 12);
    } else {
      map.fitBounds(L.latLngBounds(coords), { padding: [30, 30] });
    }
  }, [located]);

  useEffect(() => {
    const L = (window as any).L;
    if (L) {
      initMap();
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap();
    document.head.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [initMap]);

  useEffect(() => {
    if ((window as any).L) {
      initMap();
    }
  }, [initMap]);

  if (located.length === 0) return null;

  return (
    <div className="card overflow-hidden" style={{ position: 'relative', zIndex: 0 }}>
      <div className="px-4 py-2 border-b border-sand-100 flex items-center justify-between">
        <span className="text-xs font-medium text-sand-500 uppercase tracking-wide">
          Route Map — {located.length} location{located.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div ref={mapRef} style={{ height: 320, width: '100%' }} />
    </div>
  );
}


export function AdventureDetail({ adventure, user, onUpdate, onDelete }: Props) {
  const [tab, setTab] = useState<'details' | 'days'>('days');
  const [desc, setDesc] = useState(adventure.description);
  const [comment, setComment] = useState(adventure.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState<{ photos: string[]; index: number; day: string } | null>(null);
  const [locatingDay, setLocatingDay] = useState<string | null>(null);
  const [bulkLocating, setBulkLocating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [editingEntry, setEditingEntry] = useState<{ day: string; header: string; description: string } | null>(null);

  useEffect(() => {
    setDesc(adventure.description);
    setComment(adventure.comment ?? '');
    setExpandedDay(null);
    setTab('days');
  }, [adventure.id]);

  const handleSaveDetails = async () => {
    setSaving(true);
    await api.updateAdventure(adventure.id, desc, comment);
    setSaving(false);
    onUpdate();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${adventure.description}"? This cannot be undone.`)) return;
    await api.deleteAdventure(adventure.id);
    onDelete();
  };

  // ── Add new day ──
  const [newHeader, setNewHeader] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const handleAddDay = async () => {
    if (!newHeader.trim() || !newDesc.trim()) return;
    const nums = adventure.entries.map(e => parseInt(e.day) || 0);
    const next = (Math.max(0, ...nums) + 10).toString().padStart(3, '0');
    const newEntry: Entry = { day: next, header: newHeader.trim(), description: newDesc.trim(), photos: [] };
    setSaving(true);
    await api.updateEntries(adventure.id, [newEntry]); // only send the new entry to avoid merge duplicates
    setNewHeader(''); setNewDesc('');
    setSaving(false);
    onUpdate();
  };

  // ── Reorder days (pointer-based, not HTML5 DnD) ──
  // HTML5 draggable/onDragStart/onDrop -- what this used to be -- has
  // never worked for touch input on iOS Safari; it's a mouse-only API
  // there. That silently broke day-reordering on every phone this app is
  // actually used from, with no error, just a drag that does nothing.
  // Pointer Events work on both, and this is the same pattern already
  // proven out for the dashboard's adventure-list reordering.
  const [reordering, setReordering] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const entriesRef = useRef(adventure.entries);
  entriesRef.current = adventure.entries;
  const dayDragRef = useRef<{
    idx: number;
    offsetY: number;
    clone: HTMLElement;
    container: HTMLElement;
    card: HTMLElement;
    currentOver: number;
    moved: boolean;
  } | null>(null);

  const startDayDrag = (e: React.PointerEvent, index: number) => {
    if (e.button !== 0) return;
    const card = (e.currentTarget as HTMLElement).closest('[data-day-item]') as HTMLElement;
    if (!card) return;
    const container = card.closest('[data-day-list]') as HTMLElement;
    if (!container) return;

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const cardRect = card.getBoundingClientRect();

    const clone = card.cloneNode(true) as HTMLElement;
    clone.style.cssText = `
      position: fixed;
      left: ${cardRect.left}px;
      top: ${cardRect.top}px;
      width: ${cardRect.width}px;
      z-index: 9999;
      opacity: 0.9;
      box-shadow: 0 8px 24px rgba(0,0,0,0.18);
      pointer-events: none;
      transition: none;
      border-radius: 8px;
    `;
    document.body.appendChild(clone);
    card.style.opacity = '0.2';

    dayDragRef.current = {
      idx: index,
      offsetY: e.clientY - cardRect.top,
      clone,
      container,
      card,
      currentOver: index,
      moved: false,
    };
    setDragIdx(index);
    e.preventDefault();
  };

  const moveDayDrag = (e: React.PointerEvent) => {
    const d = dayDragRef.current;
    if (!d) return;
    e.preventDefault();
    d.moved = true;
    d.clone.style.top = `${e.clientY - d.offsetY}px`;

    const cards = Array.from(d.container.querySelectorAll('[data-day-item]'));
    const centerY = e.clientY;
    let overIdx = d.currentOver;
    for (let ci = 0; ci < cards.length; ci++) {
      const r = cards[ci].getBoundingClientRect();
      const midY = r.top + r.height / 2;
      if (centerY < midY) { overIdx = ci; break; }
      if (ci === cards.length - 1) overIdx = ci;
    }
    if (overIdx !== d.currentOver) {
      d.currentOver = overIdx;
      setDragOverIdx(overIdx);
    }
  };

  const endDayDrag = async (e: React.PointerEvent) => {
    const d = dayDragRef.current;
    if (!d) return;
    if (d.clone.parentElement) d.clone.parentElement.removeChild(d.clone);
    d.card.style.opacity = '1';

    const fromIdx = d.idx;
    const toIdx = d.currentOver;
    dayDragRef.current = null;
    setDragIdx(null);
    setDragOverIdx(null);

    if (d.moved && fromIdx !== toIdx) {
      const entries = [...entriesRef.current];
      const [moved] = entries.splice(fromIdx, 1);
      entries.splice(toIdx, 0, moved);
      setReordering(true);
      await api.updateEntries(adventure.id, entries, true);
      setReordering(false);
      onUpdate();
    }
  };

  // ── Reorder photos within a day (same pointer-based pattern as day
  // reordering above, plus two things a 2D grid needs that a 1D list
  // doesn't: nearest-CELL detection (by 2D distance, not just Y) since
  // rows wrap, and edge auto-scroll -- a day can carry 40+ photos, more
  // than fits on screen, so dragging has to be able to scroll the page
  // while the pointer holds near the top/bottom edge instead of being
  // limited to whatever's currently visible. ──
  const [photoDragOver, setPhotoDragOver] = useState<{ day: string; index: number } | null>(null);
  const photoDragRef = useRef<{
    day: string;
    idx: number;
    offsetX: number;
    offsetY: number;
    clone: HTMLElement;
    grid: HTMLElement;
    currentOver: number;
    moved: boolean;
    scrollDir: number; // -1 up, 0 none, 1 down
    scrollRaf: number | null;
  } | null>(null);

  const runPhotoAutoScroll = () => {
    const d = photoDragRef.current;
    if (!d || d.scrollDir === 0) return;
    window.scrollBy(0, d.scrollDir * 14);
    d.scrollRaf = requestAnimationFrame(runPhotoAutoScroll);
  };

  const startPhotoDrag = (e: React.PointerEvent, day: string, index: number) => {
    if (e.button !== 0) return;
    const tile = (e.currentTarget as HTMLElement).closest('[data-photo-item]') as HTMLElement;
    if (!tile) return;
    const grid = tile.closest('[data-photo-grid]') as HTMLElement;
    if (!grid) return;

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const rect = tile.getBoundingClientRect();

    const clone = tile.cloneNode(true) as HTMLElement;
    clone.style.cssText = `
      position: fixed;
      left: ${rect.left}px;
      top: ${rect.top}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      z-index: 9999;
      opacity: 0.9;
      box-shadow: 0 8px 24px rgba(0,0,0,0.18);
      pointer-events: none;
      transition: none;
      border-radius: 8px;
    `;
    document.body.appendChild(clone);
    tile.style.opacity = '0.2';

    photoDragRef.current = {
      day, idx: index,
      offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
      clone, grid,
      currentOver: index,
      moved: false,
      scrollDir: 0,
      scrollRaf: null,
    };
    setPhotoDragOver({ day, index });
    e.preventDefault();
  };

  const movePhotoDrag = (e: React.PointerEvent) => {
    const d = photoDragRef.current;
    if (!d) return;
    e.preventDefault();
    d.moved = true;
    d.clone.style.left = `${e.clientX - d.offsetX}px`;
    d.clone.style.top = `${e.clientY - d.offsetY}px`;

    // Edge auto-scroll -- within 60px of the viewport top/bottom, scroll
    // the page while the pointer holds there. The clone is position:fixed
    // so it stays visually under the pointer on its own as the page
    // scrolls underneath it; only the drop-target calculation below needs
    // to re-run against each new scroll position, which it already does
    // every pointermove.
    const EDGE = 60;
    const newDir = e.clientY < EDGE ? -1 : e.clientY > window.innerHeight - EDGE ? 1 : 0;
    if (newDir !== d.scrollDir) {
      d.scrollDir = newDir;
      if (newDir !== 0 && d.scrollRaf === null) {
        d.scrollRaf = requestAnimationFrame(runPhotoAutoScroll);
      } else if (newDir === 0 && d.scrollRaf !== null) {
        cancelAnimationFrame(d.scrollRaf);
        d.scrollRaf = null;
      }
    }

    // Nearest cell by 2D distance from the pointer to each tile's center
    // -- a grid wraps rows, so "which one is the pointer over" isn't a
    // single-axis comparison the way the day list's vertical stack is.
    const tiles = Array.from(d.grid.querySelectorAll('[data-photo-item]'));
    let best = d.currentOver, bestDist = Infinity;
    tiles.forEach((t, ti) => {
      const r = t.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const dist = (e.clientX - cx) ** 2 + (e.clientY - cy) ** 2;
      if (dist < bestDist) { bestDist = dist; best = ti; }
    });
    if (best !== d.currentOver) {
      d.currentOver = best;
      setPhotoDragOver({ day: d.day, index: best });
    }
  };

  const endPhotoDrag = async (e: React.PointerEvent, entry: Entry) => {
    const d = photoDragRef.current;
    if (!d) return;
    if (d.scrollRaf !== null) cancelAnimationFrame(d.scrollRaf);
    if (d.clone.parentElement) d.clone.parentElement.removeChild(d.clone);
    const tile = d.grid.querySelectorAll('[data-photo-item]')[d.idx] as HTMLElement | undefined;
    if (tile) tile.style.opacity = '1';

    const fromIdx = d.idx;
    const toIdx = d.currentOver;
    photoDragRef.current = null;
    setPhotoDragOver(null);

    if (d.moved && fromIdx !== toIdx) {
      // Look up the entry fresh (not the closed-over `entry` prop) in case
      // it changed since the drag started, same reasoning as
      // entriesRef.current in endDayDrag.
      const current = entriesRef.current.find(en => en.day === entry.day) || entry;
      const photos = [...current.photos];
      const [moved] = photos.splice(fromIdx, 1);
      photos.splice(toIdx, 0, moved);
      setSaving(true);
      await api.updateEntries(adventure.id, [{ ...current, photos }]);
      setSaving(false);
      onUpdate();
    }
  };

  // ── Photo upload ──
  // Keyed by day, so retrying one entry's failures doesn't touch another
  // expanded entry's. Holds the actual File objects (not just names) --
  // a failed upload's file was never sent anywhere, so retrying it just
  // means running the exact same file back through uploadPhotoDual, no
  // need to ask the user to re-pick it from their camera roll.
  const [failedUploads, setFailedUploads] = useState<Record<string, { file: File; error: string }[]>>({});

  const handlePhotoUpload = async (entry: Entry, rawFileList: File[]) => {
    // Nothing dedupes the picker's selection against this day's existing
    // photos -- re-picking an overlapping photo (easy to do scrolling
    // back through a 40+ photo camera roll) used to re-upload it and add
    // a second array entry with the identical filename, which is a real
    // problem: photo tiles are keyed by filename (React key collision),
    // and deleting one copy by index could leave the other in a confused
    // state. Filter those out before ever starting an upload.
    const existing = new Set(entry.photos);
    const fileList = rawFileList.filter(f => !existing.has(f.name.replace(/[^a-zA-Z0-9._-]/g, '_')));
    const skipped = rawFileList.length - fileList.length;
    if (skipped > 0) {
      alert(`${skipped} photo(s) already added to this day were skipped.`);
    }
    if (fileList.length === 0) return;

    setUploadingPhotos(true);
    setUploadProgress({ done: 0, total: fileList.length });
    const newPhotos = [...entry.photos];
    const failures: { file: File; error: string }[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const filename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const result = await api.uploadPhotoDual(user.s3_prefix, adventure.id, entry.day, filename, file);
      if (result.ok) newPhotos.push(filename);
      else failures.push({ file, error: result.error || 'unknown error' });
      setUploadProgress({ done: i + 1, total: fileList.length });
    }
    setUploadingPhotos(false);
    setSaving(true);
    const updatedEntry = { ...entry, photos: newPhotos };
    await api.updateEntries(adventure.id, [updatedEntry]);
    setSaving(false);
    onUpdate();
    // Replaces (not appends) this day's failed list -- a retry either
    // succeeds (this ends up empty, clearing the banner) or fails again
    // (recorded fresh), never both an old and a new copy of the same file.
    setFailedUploads(prev => ({ ...prev, [entry.day]: failures }));
    // Uploads used to fail completely silently -- same file list, same
    // "nothing happened" outcome whether 0 or all of them made it, with no
    // way to tell why without opening devtools. Surface it the same way
    // every other async failure in this component already does (see
    // handleExtractLocation above).
    if (failures.length) {
      alert(`${failures.length} of ${fileList.length} photo(s) failed to upload:\n\n${failures.map(f => `${f.file.name}: ${f.error}`).join('\n')}`);
    }
  };

  const handleRetryFailedUploads = (entry: Entry) => {
    const failed = failedUploads[entry.day];
    if (!failed?.length) return;
    handlePhotoUpload(entry, failed.map(f => f.file));
  };

  const handleDeletePhoto = async (entry: Entry, photoIndex: number) => {
    const updated = { ...entry, photos: entry.photos.filter((_, i) => i !== photoIndex) };
    await api.updateEntries(adventure.id, [updated]);
    onUpdate();
  };

  // ── Inline entry editing ──
  const startEditingEntry = (entry: Entry) => {
    setEditingEntry({ day: entry.day, header: entry.header, description: entry.description });
  };

  const handleSaveEntry = async (entry: Entry) => {
    if (!editingEntry) return;
    setSaving(true);
    const updatedEntry: Entry = {
      ...entry,
      header: editingEntry.header,
      description: editingEntry.description,
    };
    await api.updateEntries(adventure.id, [updatedEntry]);
    setEditingEntry(null);
    setSaving(false);
    onUpdate();
  };

  const handleDeleteEntry = async (entry: Entry, index: number) => {
    if (!window.confirm(`Delete "${entry.header}"? This cannot be undone.`)) return;
    const remaining = adventure.entries.filter((_, i) => i !== index);
    setSaving(true);
    await api.updateEntries(adventure.id, remaining, true);
    setExpandedDay(null);
    setSaving(false);
    onUpdate();
  };

  // ── Location extraction (single) ──
  const handleExtractLocation = async (entry: Entry) => {
    setLocatingDay(entry.day);
    try {
      const result = await api.extractLocation(
        adventure.description,
        entry.header,
        entry.description,
      );
      if (result) {
        const updatedEntry: Entry = {
          ...entry,
          lat: result.lat,
          lng: result.lng,
          city: result.city,
          country: result.country,
        };
        await api.updateEntries(adventure.id, [updatedEntry]);
        onUpdate();
      } else {
        alert('Could not determine location from this entry. Try adding more detail to the header or description.');
      }
    } catch (err) {
      console.error('Location extraction failed:', err);
      alert('Location extraction failed. Please try again.');
    } finally {
      setLocatingDay(null);
    }
  };

  const handleClearLocation = async (entry: Entry) => {
    const updatedEntry: Entry = {
      ...entry,
      lat: undefined,
      lng: undefined,
      city: undefined,
      country: undefined,
    };
    await api.updateEntries(adventure.id, [updatedEntry]);
    onUpdate();
  };

  // ── Bulk location extraction ──
  const handleGenerateAllLocations = async () => {
    const missing = adventure.entries.filter(e => e.lat == null || e.lng == null);
    if (missing.length === 0) return;

    if (!window.confirm(`Generate locations for ${missing.length} day${missing.length !== 1 ? 's' : ''} without coordinates?\nThis takes ~${Math.ceil(missing.length * 2)} seconds.`)) return;

    setBulkLocating(true);
    setBulkProgress({ done: 0, total: missing.length });

    const DELAY_MS = 1500;
    const allLocated: Entry[] = [];

    for (let i = 0; i < missing.length; i++) {
      const entry = missing[i];

      // Throttle: wait between calls to avoid Bedrock rate limits
      if (i > 0) {
        await new Promise(r => setTimeout(r, DELAY_MS));
      }

      // Try up to 2 times per entry
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const result = await api.extractLocation(
            adventure.description,
            entry.header,
            entry.description,
          );
          if (result) {
            allLocated.push({
              ...entry,
              lat: result.lat,
              lng: result.lng,
              city: result.city,
              country: result.country,
            });
            console.log(`📍 ${i + 1}/${missing.length} ${entry.header} → ${result.city} (${result.lat}, ${result.lng})`);
          }
          break;
        } catch (err) {
          console.error(`Attempt ${attempt + 1} failed for "${entry.header}":`, err);
          if (attempt === 0) {
            await new Promise(r => setTimeout(r, 3000));
          }
        }
      }
      setBulkProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }

    // Single save at the end — avoids DynamoDB read-modify-write race
    if (allLocated.length > 0) {
      console.log(`📍 Saving all ${allLocated.length} locations in one call`);
      const saveOk = await api.updateEntries(adventure.id, allLocated);
      console.log(`📍 Save result: ${saveOk}`);
    }

    setBulkLocating(false);
    onUpdate();
    if (allLocated.length < missing.length) {
      alert(`Located ${allLocated.length} of ${missing.length} entries. Some entries may need more descriptive headers.`);
    }
  };

  const locatedCount = adventure.entries.filter(e => e.lat != null && e.lng != null).length;
  const missingCount = adventure.entries.length - locatedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-sand-900 truncate">{adventure.description}</h2>
        <span className="text-xs text-sand-400 bg-sand-100 px-2 py-1 rounded font-mono">ID: {adventure.id}</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-sand-100 rounded-lg p-1">
        {(['days', 'details'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all capitalize
              ${tab === t ? 'bg-white text-sand-900 shadow-sm' : 'text-sand-500 hover:text-sand-700'}`}>
            {t === 'days' ? `Days (${adventure.entries.length})` : 'Details'}
          </button>
        ))}
      </div>

      {/* Days tab */}
      {tab === 'days' && (
        <div className="space-y-2" data-day-list>

          {/* Route map */}
          <AdventureMap entries={adventure.entries} />

          {/* Bulk location bar */}
          {adventure.entries.length > 0 && (
            <div className="card px-4 py-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs text-sand-500">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline -mt-0.5 mr-1">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                {locatedCount} of {adventure.entries.length} days located
              </span>
              {bulkLocating ? (
                <span className="text-xs text-sand-500 flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-sand-300 border-t-sand-600 rounded-full animate-spin" />
                  Extracting {bulkProgress.done}/{bulkProgress.total}…
                </span>
              ) : missingCount > 0 ? (
                <button
                  onClick={handleGenerateAllLocations}
                  className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  Generate All Locations ({missingCount})
                </button>
              ) : (
                <span className="text-xs text-emerald-600">✓ All days located</span>
              )}
            </div>
          )}

          {adventure.entries.length === 0 && (
            <div className="card p-6 text-center text-sand-400">
              <p>No days yet. Add one below.</p>
            </div>
          )}

          {adventure.entries.map((entry, i) => (
            <div
              key={entry.day}
              data-day-item
              className={`card overflow-hidden transition-all ${
                dragOverIdx === i && dragIdx !== null && dragIdx !== i ? 'ring-2 ring-adventure/50 bg-adventure/5' : ''
              } ${dragIdx === i ? 'opacity-40' : ''}`}
            >
              <div className="flex items-center">
                {/* Drag handle */}
                <div
                  onPointerDown={e => startDayDrag(e, i)}
                  onPointerMove={moveDayDrag}
                  onPointerUp={endDayDrag}
                  onPointerCancel={endDayDrag}
                  className="px-2 cursor-grab active:cursor-grabbing border-r border-sand-100 self-stretch flex items-center text-sand-300 hover:text-sand-500 transition-colors touch-none select-none">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="5" r="1" fill="currentColor" stroke="none"/>
                    <circle cx="9" cy="12" r="1" fill="currentColor" stroke="none"/>
                    <circle cx="9" cy="19" r="1" fill="currentColor" stroke="none"/>
                    <circle cx="15" cy="5" r="1" fill="currentColor" stroke="none"/>
                    <circle cx="15" cy="12" r="1" fill="currentColor" stroke="none"/>
                    <circle cx="15" cy="19" r="1" fill="currentColor" stroke="none"/>
                  </svg>
                </div>
                {/* Day header */}
                <button onClick={() => setExpandedDay(expandedDay === i ? null : i)}
                  className="flex-1 text-left px-4 py-3 flex items-center justify-between hover:bg-sand-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sand-900">{entry.header}</span>
                    {entry.lat != null && entry.lng != null && (
                      <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" title={`${entry.city}, ${entry.country}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline -mt-0.5 mr-0.5">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        {entry.city || `${entry.lat.toFixed(1)},${entry.lng.toFixed(1)}`}
                      </span>
                    )}
                    <span className="text-sand-400 text-sm">
                      {entry.photos.length} photo{entry.photos.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className={`text-sand-400 transition-transform ${expandedDay === i ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>

              {expandedDay === i && (
                <div className="px-4 pb-4 border-t border-sand-100 pt-3 space-y-3">
                  {/* Inline editing */}
                  {editingEntry?.day === entry.day ? (
                    <div className="space-y-2">
                      <input
                        className="input text-sm"
                        value={editingEntry.header}
                        onChange={e => setEditingEntry({ ...editingEntry, header: e.target.value })}
                        placeholder="Title"
                      />
                      <textarea
                        className="input text-sm min-h-[80px]"
                        value={editingEntry.description}
                        onChange={e => setEditingEntry({ ...editingEntry, description: e.target.value })}
                        placeholder="Description"
                      />
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveEntry(entry)} disabled={saving}
                          className="btn-primary text-xs py-1.5 px-3">
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={() => setEditingEntry(null)}
                          className="btn-secondary text-xs py-1.5 px-3">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-sand-600 flex-1">{entry.description || <span className="italic text-sand-300">No description</span>}</p>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => startEditingEntry(entry)}
                          className="text-xs text-blue-500 hover:text-blue-700 transition-colors px-2 py-1 rounded hover:bg-blue-50"
                          title="Edit">
                          ✎ Edit
                        </button>
                        <button onClick={() => handleDeleteEntry(entry, i)}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-red-50"
                          title="Delete day">
                          Delete
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Location info + actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {entry.lat != null && entry.lng != null ? (
                      <>
                        <span className="text-xs text-sand-500 bg-sand-50 px-2 py-1 rounded border border-sand-200">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline -mt-0.5 mr-1">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                          </svg>
                          {entry.city && entry.country
                            ? `${entry.city}, ${entry.country}`
                            : `${entry.lat.toFixed(4)}, ${entry.lng.toFixed(4)}`}
                        </span>
                        <span className="text-xs text-sand-400">
                          ({entry.lat.toFixed(4)}, {entry.lng.toFixed(4)})
                        </span>
                        <button
                          onClick={() => handleExtractLocation(entry)}
                          disabled={locatingDay === entry.day}
                          className="text-xs text-blue-500 hover:text-blue-700 transition-colors disabled:opacity-50"
                          title="Re-extract location">
                          {locatingDay === entry.day ? '...' : '↻'}
                        </button>
                        <button
                          onClick={() => handleClearLocation(entry)}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors"
                          title="Clear location">
                          ✕
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleExtractLocation(entry)}
                        disabled={locatingDay === entry.day}
                        className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-50">
                        {locatingDay === entry.day ? (
                          <>
                            <span className="w-3 h-3 border-2 border-sand-300 border-t-sand-600 rounded-full animate-spin" />
                            Extracting…
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                            Generate Location
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Add Photos, repeated above the grid -- a day with a
                      lot of photos otherwise means scrolling all the way
                      down just to add one more. */}
                  {entry.photos.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="btn-secondary text-sm py-2 cursor-pointer inline-flex">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        Add Photos
                        <input type="file" multiple accept="image/*" className="hidden"
                          onChange={e => e.target.files && handlePhotoUpload(entry, Array.from(e.target.files))} />
                      </label>
                    </div>
                  )}

                  {/* Photo grid */}
                  {entry.photos.length > 0 && (
                    <div data-photo-grid className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {entry.photos.map((photo, pi) => (
                        <div key={photo} data-photo-item className={`relative group aspect-square ${
                          photoDragOver?.day === entry.day && photoDragOver.index === pi ? 'ring-2 ring-adventure' : ''
                        }`}>
                          <img
                            src={api.photoURL(user.s3_prefix, adventure.id, entry.day, photo)}
                            alt={photo}
                            className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy"
                            onClick={() => setLightbox({ photos: entry.photos, index: pi, day: entry.day })}
                          />
                          {/* Drag handle, separate from the image above so a
                              quick tap still opens the lightbox and only a
                              press-drag on this grip reorders. */}
                          <div
                            onPointerDown={e => startPhotoDrag(e, entry.day, pi)}
                            onPointerMove={movePhotoDrag}
                            onPointerUp={e => endPhotoDrag(e, entry)}
                            onPointerCancel={e => endPhotoDrag(e, entry)}
                            className="absolute top-1 left-1 w-7 h-7 rounded-full bg-black/60 text-white
                                     flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100
                                     transition-opacity text-xs shadow-md cursor-grab touch-none select-none"
                          >⠿</div>
                          <button onClick={() => handleDeletePhoto(entry, pi)}
                            className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-600 text-white
                                     flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100
                                     transition-opacity text-xs shadow-md">✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="btn-secondary text-sm py-2 cursor-pointer inline-flex">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      Add Photos
                      <input type="file" multiple accept="image/*" className="hidden"
                        onChange={e => e.target.files && handlePhotoUpload(entry, Array.from(e.target.files))} />
                    </label>
                    {!!failedUploads[entry.day]?.length && (
                      <button onClick={() => handleRetryFailedUploads(entry)} disabled={uploadingPhotos}
                        className="text-sm py-2 px-3 rounded-lg border border-red-300 bg-red-50 text-red-700
                                 hover:bg-red-100 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                        </svg>
                        Retry {failedUploads[entry.day].length} failed
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add new day */}
          <div className="card p-4 space-y-3">
            <h3 className="text-sm font-medium text-sand-500 uppercase tracking-wide">Add New Day</h3>
            <input className="input" placeholder="Title" value={newHeader} onChange={e => setNewHeader(e.target.value)} />
            <input className="input" placeholder="Description" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
            <button onClick={handleAddDay} disabled={!newHeader.trim() || !newDesc.trim() || saving}
              className="btn-primary text-sm">
              Add Day
            </button>
          </div>
        </div>
      )}

      {/* Details tab */}
      {tab === 'details' && (
        <div className="card p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-sand-600 mb-1">Description</label>
            <input className="input" value={desc} onChange={e => setDesc(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-sand-600 mb-1">Comment</label>
            <textarea className="input min-h-[200px]" value={comment} onChange={e => setComment(e.target.value)} />
          </div>
          <div className="flex gap-3">
            <button onClick={handleSaveDetails} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={handleDelete} className="btn-danger">Delete Adventure</button>
          </div>
        </div>
      )}

      {/* Upload indicator -- deliberately much louder than the plain
          saving/locating pill below: solid brand-color background, a real
          progress bar (not just a number), bigger text. Easy to miss the
          small gray pill when your thumb's still on the photo picker or
          you're scrolled away from the bottom of the screen; this is
          meant to be impossible to miss. */}
      {uploadingPhotos && (
        <div className="fixed bottom-6 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-80 bg-adventure text-white rounded-xl px-5 py-4 shadow-2xl z-50">
          <div className="flex items-center gap-3 mb-2.5">
            <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin flex-shrink-0" />
            <span className="text-base font-semibold">
              Uploading photo {uploadProgress.done} of {uploadProgress.total}
            </span>
          </div>
          <div className="h-2 bg-white/25 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Saving / bulk indicator */}
      {!uploadingPhotos && (saving || bulkLocating) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 card px-4 py-2 flex items-center gap-2 shadow-lg z-50">
          <span className="w-4 h-4 border-2 border-adventure/30 border-t-adventure rounded-full animate-spin" />
          <span className="text-sm text-sand-600">
            {bulkLocating ? `Locating ${bulkProgress.done}/${bulkProgress.total}…` : 'Saving...'}
          </span>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white z-10 p-2"
            onClick={() => setLightbox(null)}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <div className="absolute top-4 left-4 text-white/60 text-sm">
            {lightbox.index + 1} / {lightbox.photos.length}
          </div>
          {lightbox.index > 0 && (
            <button className="absolute left-2 sm:left-6 text-white/60 hover:text-white p-2 z-10"
              onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, index: lightbox.index - 1 }); }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
          )}
          <img
            src={api.fullPhotoURL(user.s3_prefix, adventure.id, lightbox.photos[lightbox.index])}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {lightbox.index < lightbox.photos.length - 1 && (
            <button className="absolute right-2 sm:right-6 text-white/60 hover:text-white p-2 z-10"
              onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, index: lightbox.index + 1 }); }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
